import { useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import type { AppShellProps, ShellAxes } from './types.js';
import { PRESET_AXES } from './types.js';

const DEFAULTS: Required<ShellAxes> = {
  shellStyle: 'floating',
  shellGap: 12,
  topBar: 'standard',
  sidebarPosition: 'left',
  sidebarWidth: 260,
  rightPanelPosition: 'right',
  rightPanelWidth: 280,
  statusBarVisibility: 'visible',
  pageWidth: 'normal',
  pageMargin: 'normal',
  pageColumns: 1,
  pageLineHeight: 'standard',
};

function resolveAxes(props: AppShellProps): Required<ShellAxes> {
  const preset = props.preset === undefined ? {} : PRESET_AXES[props.preset];
  const resolved: Required<ShellAxes> = { ...DEFAULTS, ...preset };
  const keys = Object.keys(DEFAULTS) as Array<keyof ShellAxes>;
  for (const k of keys) {
    const val = props[k];
    if (val !== undefined) (resolved as Record<string, unknown>)[k] = val;
  }
  return resolved;
}

/** Reusable floating-island layout for ViquardDoc editor apps.
 *
 *  Produces a topbar row + sidebar/main/inspector body row + optional
 *  status bar strip. Each slot is a bare grid cell; visual chrome
 *  (glass, halo, radius, shadow) is expressed via the `shellStyle`,
 *  `topBar`, `sidebarPosition`, `pageWidth` axes — consumers drop content
 *  in and the shell publishes `data-*` attributes that CSS variables key
 *  off.
 *
 *  Presets (`writing` / `reading` / `focus` / …) bundle axis defaults for
 *  common personas; explicit axis props still win. Spec 09. */
export function AppShell(props: AppShellProps): ReactNode {
  const axes = useMemo(() => resolveAxes(props), [props]);
  const {
    topLeft,
    topCenter,
    topRight,
    sidebar,
    main,
    inspector,
    statusBar,
    mainLabel,
    mainId = 'vq-main',
    className,
    sidebarDrawerOpen,
    onSidebarDrawerClose,
  } = props;

  const drawerControlled = sidebarDrawerOpen !== undefined;

  useEffect(() => {
    if (!drawerControlled || !sidebarDrawerOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onSidebarDrawerClose?.();
    };
    const prev = document.body.getAttribute('data-drawer-open');
    document.body.setAttribute('data-drawer-open', 'true');
    window.addEventListener('keydown', onKey);
    return () => {
      if (prev === null) document.body.removeAttribute('data-drawer-open');
      else document.body.setAttribute('data-drawer-open', prev);
      window.removeEventListener('keydown', onKey);
    };
  }, [drawerControlled, sidebarDrawerOpen, onSidebarDrawerClose]);

  const rootClass = ['vq-app-shell', className].filter(Boolean).join(' ');

  const hasTopbar =
    axes.topBar !== 'hidden' &&
    (topLeft !== undefined || topCenter !== undefined || topRight !== undefined);
  const hasSidebar = sidebar !== undefined && axes.sidebarPosition !== 'hidden';
  const hasInspector = inspector !== undefined && axes.rightPanelPosition !== 'hidden';
  const showSidebarCollapsed = axes.sidebarPosition === 'collapsed';
  const showInspectorCollapsed = axes.rightPanelPosition === 'collapsed';
  const hasStatusBar = statusBar !== undefined && axes.statusBarVisibility === 'visible';

  const rootStyle: CSSProperties & Record<string, unknown> = {
    '--vq-shell-gap': `${String(axes.shellGap)}px`,
    '--vq-shell-left': showSidebarCollapsed ? '0px' : `${String(axes.sidebarWidth)}px`,
    '--vq-shell-right': showInspectorCollapsed ? '0px' : `${String(axes.rightPanelWidth)}px`,
  };

  return (
    <div
      className={rootClass}
      data-component="AppShell"
      data-shell-style={axes.shellStyle}
      data-topbar={axes.topBar}
      data-sidebar={axes.sidebarPosition}
      data-rightpanel={axes.rightPanelPosition}
      data-statusbar={axes.statusBarVisibility}
      data-page-width={axes.pageWidth}
      data-page-margin={axes.pageMargin}
      data-page-columns={String(axes.pageColumns)}
      data-page-lineheight={axes.pageLineHeight}
      data-preset={props.preset}
      data-drawer-open={sidebarDrawerOpen === true ? 'true' : 'false'}
      style={rootStyle}
    >
      {hasTopbar && (
        <header className="vq-app-shell__topbar">
          {topLeft !== undefined && <div className="vq-app-shell__top-left">{topLeft}</div>}
          <div className="vq-app-shell__top-center">{topCenter}</div>
          {topRight !== undefined && <div className="vq-app-shell__top-right">{topRight}</div>}
        </header>
      )}
      <div
        className="vq-app-shell__body"
        data-has-sidebar={hasSidebar ? 'true' : 'false'}
        data-has-inspector={hasInspector ? 'true' : 'false'}
      >
        {hasSidebar && (
          <>
            <nav
              className="vq-app-shell__sidebar"
              data-open={sidebarDrawerOpen === true ? 'true' : 'false'}
            >
              {sidebar}
            </nav>
            {drawerControlled && (
              <button
                type="button"
                aria-hidden={!sidebarDrawerOpen}
                tabIndex={-1}
                className="vq-app-shell__scrim"
                onClick={() => onSidebarDrawerClose?.()}
              />
            )}
          </>
        )}
        <main id={mainId} className="vq-app-shell__main" aria-label={mainLabel} tabIndex={-1}>
          {main}
        </main>
        {hasInspector && <aside className="vq-app-shell__inspector">{inspector}</aside>}
      </div>
      {hasStatusBar && <footer className="vq-app-shell__statusbar">{statusBar}</footer>}
    </div>
  );
}
